/**
 * PostCallReassignPrompt
 *
 * Polls /api/twilio/contacts/post-call-prompts every 8s. When the user has
 * called a contact already individually-assigned to a lower-tier teammate
 * (per the role hierarchy), they get a popup:
 *   "Take this contact? / Do not take"
 *
 * Product rules (updated 2026-04-30):
 *   - No option is pre-selected — the user MUST explicitly click either
 *     a duration (to take) or "Do not take" (to leave the contact with
 *     the current owner).
 *   - "Do not take" dismisses the prompt (calls /dismiss). The server
 *     then marks it consumed so it won't re-surface on the next poll.
 *   - As a backstop, the server still auto-applies a 3-day steal if the
 *     prompt is bypassed entirely (e.g., user force-quits) via
 *     `post_call_prompts.auto_default_at`.
 *
 * Hidden for affiliates entirely (server-side 403, but we also don't poll).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const DURATIONS = [
  { id: '1d', label: '1 day' },
  { id: '3d', label: '3 days' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: 'permanent', label: 'Permanent' },
];

const PostCallReassignPrompt = () => {
  const { api, user } = useAuth();
  const [pending, setPending] = useState([]);
  const [active, setActive] = useState(null); // currently-shown prompt
  const [submitting, setSubmitting] = useState(false);

  const role = (user?.role || '').toLowerCase();
  const skip = !user || role === 'affiliate';

  const poll = useCallback(async () => {
    if (skip) return;
    try {
      const r = await api.get('/twilio/contacts/post-call-prompts');
      setPending(r.data.prompts || []);
    } catch (e) { /* ignore */ }
  }, [api, skip]);

  useEffect(() => {
    if (skip) return undefined;
    poll();
    const t = setInterval(poll, 8000);
    return () => clearInterval(t);
  }, [poll, skip]);

  // Pop the next pending prompt into active when nothing's open.
  useEffect(() => {
    if (active || skip) return;
    if (pending.length > 0) setActive(pending[0]);
  }, [pending, active, skip]);

  if (skip || !active) return null;

  const reassign = async (duration) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/twilio/contacts/${active.contact_id}/reassign`, { duration });
      toast.success(
        duration === 'permanent'
          ? 'Reassigned to you permanently'
          : `Reassigned to you for ${DURATIONS.find((d) => d.id === duration)?.label?.toLowerCase()}`
      );
      setPending((p) => p.filter((x) => x.id !== active.id));
      setActive(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reassign');
    } finally {
      setSubmitting(false);
    }
  };

  const doNotTake = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/twilio/contacts/post-call-prompts/${active.id}/dismiss`);
      toast.message(`Left with ${active.current_owner_name}`);
      setPending((p) => p.filter((x) => x.id !== active.id));
      setActive(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not dismiss');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={!!active}
      // Mandatory choice — user must pick a duration or "Do not take".
      // We still disable backdrop click + escape so the user explicitly
      // picks an action (otherwise the server's auto-default could kick
      // in unexpectedly).
      onOpenChange={() => { /* no-op: forces user to pick an option */ }}
    >
      <DialogContent
        data-testid="post-call-reassign-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        // Hide the default ✕ button rendered by the shadcn dialog.
        className="[&>button.absolute]:hidden"
      >
        <DialogHeader>
          <DialogTitle>Take this contact?</DialogTitle>
          <DialogDescription>
            <strong>{active.contact_name || active.phone_number}</strong> is currently assigned to{' '}
            <strong>{active.current_owner_name}</strong>. Choose how long to take the contact over,
            or leave it with them.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {DURATIONS.map((d) => (
            <Button
              key={d.id}
              className="w-full justify-start"
              // No default selection — every option renders as outline
              // until the user clicks one.
              variant="outline"
              disabled={submitting}
              onClick={() => reassign(d.id)}
              data-testid={`reassign-${d.id}`}
            >
              Take for {d.label}
            </Button>
          ))}
          <Button
            className="w-full justify-start border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10"
            variant="outline"
            disabled={submitting}
            onClick={doNotTake}
            data-testid="reassign-do-not-take"
          >
            Do not take — leave with {active.current_owner_name}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PostCallReassignPrompt;
